import BlackPhoto from "@/components/BlackPhoto"
import { isVideoPost } from "@/lib/filters"
import type { Post } from "@/types"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"

type MediaFrameProps = {
  post: Partial<Post>
  autoPlay?: boolean
  controls?: boolean
  preview?: boolean
  showBadge?: boolean
  fill?: boolean
  attemptUnmute?: boolean
  muted?: boolean
  playerRef?: React.RefObject<HTMLVideoElement | null>
  className?: string
}

const MediaFrame = forwardRef<HTMLVideoElement, MediaFrameProps>(function MediaFrame(
  {
    post,
    autoPlay = false,
    controls = true,
    preview = false,
    showBadge = false,
    fill = false,
    attemptUnmute = false,
    muted: mutedProp,
    playerRef,
    className = "",
  },
  ref,
) {
  const video = isVideoPost(post)
  const nodeRef = useRef<HTMLVideoElement>(null)
  const [failed, setFailed] = useState(false)
  const [muted, setMuted] = useState(mutedProp ?? true)
  const [needTap, setNeedTap] = useState(false)

  useImperativeHandle(ref, () => nodeRef.current as HTMLVideoElement)

  useEffect(() => {
    if (playerRef) playerRef.current = nodeRef.current
  })

  useEffect(() => {
    setFailed(false)
    setNeedTap(false)
    if (mutedProp === undefined) setMuted(true)
  }, [post?.videoUrl, post?.imageUrl, mutedProp])

  useEffect(() => {
    if (mutedProp !== undefined) setMuted(mutedProp)
  }, [mutedProp])

  useEffect(() => {
    const node = nodeRef.current
    if (!node || preview || !video) return
    const play = async () => {
      try {
        node.muted = mutedProp ?? true
        await node.play()
        if (attemptUnmute && mutedProp !== true) {
          node.muted = false
          setMuted(false)
        }
      } catch {
        node.muted = true
        setMuted(true)
        setNeedTap(true)
        node.play().catch(() => undefined)
      }
    }
    if (!autoPlay) {
      node.pause()
      return
    }
    play()
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) play()
          else node.pause()
        })
      },
      { threshold: 0.65 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [autoPlay, attemptUnmute, preview, video, post?.videoUrl, mutedProp])

  useEffect(() => {
    const node = nodeRef.current
    if (!node) return
    node.muted = muted
  }, [muted])

  useEffect(() => {
    const node = nodeRef.current
    if (!node || !video || autoPlay || preview) return
    const stamp = Number(post.coverTime) || 0
    node.pause()
    try {
      if (Math.abs(node.currentTime - stamp) > 0.04) node.currentTime = stamp
    } catch {
      /* Some browsers reject seeks until more data is ready. */
    }
  }, [autoPlay, preview, video, post?.coverTime, post?.videoUrl])

  const caption = (post.caption || "").trim()
  const objectFit = fill ? "object-cover" : "object-contain"

  if (preview && video && post.videoUrl && !failed) {
    const stamp = Number(post.coverTime) || 0
    const seekCover = (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const node = event.currentTarget
      node.pause()
      try {
        node.currentTime = stamp > 0.04 ? stamp : 0.08
      } catch {
        /* Some browsers reject seeks until more data is ready. */
      }
    }
    return (
      <div className={`relative h-full w-full overflow-hidden bg-black ${className}`}>
        <video
          key={`${post.videoUrl}-${stamp}`}
          src={post.videoUrl}
          poster={post.imageUrl || undefined}
          muted
          playsInline
          preload="metadata"
          className={`h-full w-full ${objectFit}`}
          onLoadedMetadata={seekCover}
          onLoadedData={seekCover}
          onError={() => setFailed(true)}
        />
        {showBadge && caption ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
            <p className="line-clamp-2 text-left text-[11px] font-medium leading-snug text-white">{caption}</p>
          </div>
        ) : null}
      </div>
    )
  }

  if (video && post.videoUrl && !failed) {
    return (
      <div className={`relative h-full w-full overflow-hidden bg-black ${className}`}>
        <video
          ref={nodeRef}
          src={post.videoUrl}
          poster={post.imageUrl || undefined}
          className={`h-full w-full ${objectFit}`}
          muted={muted}
          loop
          playsInline
          preload="auto"
          controls={controls && !fill}
          onLoadedMetadata={(event) => {
            const node = event.currentTarget
            const stamp = Number(post.coverTime) || 0
            if (stamp <= 0.04) return
            try {
              node.currentTime = stamp
            } catch {
              /* Some browsers reject seeks until more data is ready. */
            }
          }}
          onClick={() => {
            const node = nodeRef.current
            if (!node) return
            node.muted = false
            setMuted(false)
            setNeedTap(false)
            node.play().catch(() => undefined)
          }}
          onError={() => setFailed(true)}
        />
        {needTap ? (
          <button
            type="button"
            onClick={() => {
              const node = nodeRef.current
              if (!node) return
              node.muted = false
              setMuted(false)
              setNeedTap(false)
              node.play().catch(() => undefined)
            }}
            className="absolute inset-0 z-10 flex items-center justify-center bg-black/25"
          >
            <span className="rounded-full bg-black/70 px-4 py-2 text-sm font-semibold text-white">Tap for sound</span>
          </button>
        ) : null}
        {showBadge && caption ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
            <p className="line-clamp-2 text-left text-[11px] font-medium leading-snug text-white">{caption}</p>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className={`relative h-full w-full overflow-hidden bg-black ${className}`}>
      <BlackPhoto
        src={post.imageUrl}
        alt={caption || "KLIPO post"}
        filter={post.filter}
        brightness={post.brightness}
        contrast={post.contrast}
        saturate={post.saturate}
        fit={fill ? "cover" : "contain"}
        className={`h-full w-full ${objectFit}`}
      />
      {showBadge && caption ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
          <p className="line-clamp-2 text-left text-[11px] font-medium leading-snug text-white">{caption}</p>
        </div>
      ) : null}
    </div>
  )
})

export default MediaFrame
